/**
 * @fileoverview Shared schema for story structure phases.
 *
 * Defines the universal "time buckets" used to align beats across storylines
 * and character arcs. These phases represent the key structural moments that
 * define story rhythm across the Three-Act structure.
 *
 * Used by both:
 * - CharacterBeatSchema (internal/psychological shifts)
 * - StorylineBeatSchema (external plot events)
 */

import { z } from 'zod';

// =============================================================================
// STRUCTURE PHASE SCHEMA
// =============================================================================

/**
 * The structural phases that define key moments in a narrative.
 * Based on the Three-Act structure with key turning points.
 */
export const StructurePhaseSchema = z.enum([
  'Initial State',
  'Inciting Incident',
  'Break Into Two',
  'Midpoint',
  'Break Into Three',
  'Climax',
  'Final State',
]).describe(
  'The structural phase this beat falls into. Used for vertical alignment across storylines and character arcs.'
);

export type StructurePhase = z.infer<typeof StructurePhaseSchema>;

// =============================================================================
// SEMANTIC GUIDANCE
// =============================================================================

/**
 * Comprehensive semantic guidance for each structure phase.
 * Used by AI agents and UI to understand the meaning and purpose of each phase.
 */
export const STRUCTURE_PHASE_GUIDANCE: Record<
  StructurePhase,
  {
    label: string;
    act: 'Act 1' | 'Act 2' | 'Act 3';
    alternateNames: string[];
    description: string;
    characterGuidance: string;
    storylineGuidance: string;
    examples: string[];
  }
> = {
  'Initial State': {
    label: 'Initial State',
    act: 'Act 1',
    alternateNames: ['Status Quo', 'Ordinary World', 'Setup'],
    description:
      'The baseline state before the story begins. Establishes the "normal" that will be disrupted. ' +
      'Shows the world, relationships, and internal states that the story will transform.',
    characterGuidance:
      'Capture the character\'s emotional/psychological starting point. What is their worldview? ' +
      'What lie do they believe? What wound are they carrying? What do they want vs. what do they need?',
    storylineGuidance:
      'Establish the status quo for this narrative thread. What is the current state of affairs? ' +
      'What tensions or problems exist beneath the surface that will later erupt?',
    examples: [
      'Luke Skywalker dreams of adventure but is stuck on a moisture farm',
      'Indy is a professor, dismissive of supernatural claims',
      'The kingdom is at peace, but the king is secretly ill',
    ],
  },

  'Inciting Incident': {
    label: 'Inciting Incident',
    act: 'Act 1',
    alternateNames: ['Call to Adventure', 'Catalyst', 'The Hook'],
    description:
      'The event that disrupts the status quo and kicks the story into motion. ' +
      'Something happens that the protagonist cannot ignore—it demands a response.',
    characterGuidance:
      'What event challenges the character\'s worldview or forces them out of their comfort zone? ' +
      'This should create a choice or pressure that makes staying in the Initial State impossible.',
    storylineGuidance:
      'What event or revelation launches this storyline? This is the "problem" that needs solving, ' +
      'the question that needs answering, or the conflict that needs resolving.',
    examples: [
      'R2-D2 arrives with Princess Leia\'s message',
      'Army Intelligence approaches Indy about the Ark',
      'A message arrives: the enemy has crossed the border',
    ],
  },

  'Break Into Two': {
    label: 'Break Into Two',
    act: 'Act 1',
    alternateNames: ['Plot Point 1', 'Crossing the Threshold', 'Point of No Return', 'The Commitment'],
    description:
      'The protagonist commits to the journey. They leave the familiar world behind and enter ' +
      'unfamiliar territory (literal or metaphorical). There is no going back after this point.',
    characterGuidance:
      'What choice or action shows the character committing to change? This often involves leaving ' +
      'something behind (home, safety, old beliefs) and stepping into uncertainty.',
    storylineGuidance:
      'The storyline shifts from setup to action. The protagonist is now actively pursuing the goal. ' +
      'Stakes are established and the central conflict is fully engaged.',
    examples: [
      'Luke leaves Tatooine with Obi-Wan',
      'Indy flies to Nepal to find Marion and the medallion',
      'The hero accepts the quest and rides out with their companions',
    ],
  },

  'Midpoint': {
    label: 'Midpoint',
    act: 'Act 2',
    alternateNames: ['Midpoint Shift', 'Point of No Return', 'Revelation', 'Mirror Moment'],
    description:
      'A major event that shifts the protagonist from reactive to proactive. Often involves a ' +
      'revelation, a false victory/defeat, or a moment of clarity. The stakes are raised dramatically.',
    characterGuidance:
      'The character experiences a shift in understanding. They stop merely reacting and start ' +
      'actively pursuing their goal. Often involves glimpsing a truth about themselves or the world.',
    storylineGuidance:
      'The narrative pivots. What was hidden is revealed. What seemed certain is questioned. ' +
      'The protagonist\'s approach must change because the rules of the game have changed.',
    examples: [
      'The Death Star destroys Alderaan—this is now about more than rescue',
      'Indy finds the Ark but the Nazis take it',
      'The hero discovers the mentor is actually the villain\'s sibling',
    ],
  },

  'Break Into Three': {
    label: 'Break Into Three',
    act: 'Act 2',
    alternateNames: ['All Is Lost', 'Dark Night of the Soul', 'Crisis', 'The Ordeal'],
    description:
      'The lowest point. Everything seems lost. The protagonist faces their greatest fear or ' +
      'suffers their biggest setback. From this despair, they must find the strength to rally.',
    characterGuidance:
      'The character confronts their deepest wound or greatest fear. The lie they\'ve believed is ' +
      'fully exposed. They must choose: retreat to old patterns or embrace painful truth.',
    storylineGuidance:
      'The storyline reaches its darkest moment. The goal seems impossible. But this crisis ' +
      'contains the seed of the solution—often through a synthesis of lessons learned.',
    examples: [
      'Obi-Wan dies; the Death Star approaches the rebel base',
      'Marion appears to die in the explosion',
      'The army is scattered, the hero wounded, all allies captured',
    ],
  },

  'Climax': {
    label: 'Climax',
    act: 'Act 3',
    alternateNames: ['Final Battle', 'Showdown', 'Resolution', 'The Confrontation'],
    description:
      'The protagonist faces the central conflict head-on. All storylines converge. ' +
      'The character must prove their transformation by making a defining choice under pressure.',
    characterGuidance:
      'The character demonstrates their growth by making a choice that their Initial State self ' +
      'could not have made. They act on the truth they\'ve learned, even at great cost.',
    storylineGuidance:
      'The central question of the storyline is answered. The conflict is resolved one way or ' +
      'another. Multiple storylines often braid together here, with lower-priority stories ' +
      'unlocking the resolution of higher-priority ones.',
    examples: [
      'Luke trusts the Force and destroys the Death Star',
      'Indy chooses to close his eyes, trusting faith over knowledge',
      'The hero faces the villain with their new understanding, using their allies\' gifts',
    ],
  },

  'Final State': {
    label: 'Final State',
    act: 'Act 3',
    alternateNames: ['Resolution', 'New Normal', 'Denouement', 'Return with Elixir'],
    description:
      'The new equilibrium after the climax. Shows how the world and characters have changed. ' +
      'Contrasts with Initial State to demonstrate the story\'s transformation.',
    characterGuidance:
      'The character\'s new normal. How have they changed? What truth do they now embody? ' +
      'What wound has healed (or scarred)? This should echo but contrast with Initial State.',
    storylineGuidance:
      'The storyline\'s resolution. What is the new status quo? How has the world changed? ' +
      'Loose threads are tied up, and the implications of the climax are shown.',
    examples: [
      'Luke is celebrated as a hero of the Rebellion',
      'The Ark is boxed up in a warehouse; Indy and Marion walk off together',
      'The kingdom is at peace under new leadership; the hero has found their true home',
    ],
  },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Returns the structure phases in narrative order.
 */
export function getStructurePhasesInOrder(): StructurePhase[] {
  return [
    'Initial State',
    'Inciting Incident',
    'Break Into Two',
    'Midpoint',
    'Break Into Three',
    'Climax',
    'Final State',
  ];
}

/**
 * Gets the act for a given structure phase.
 */
export function getActForPhase(phase: StructurePhase): 'Act 1' | 'Act 2' | 'Act 3' {
  return STRUCTURE_PHASE_GUIDANCE[phase].act;
}

/**
 * Returns the index (0-6) of a phase in narrative order.
 * Useful for sorting and comparison.
 */
export function getPhaseIndex(phase: StructurePhase): number {
  return getStructurePhasesInOrder().indexOf(phase);
}

// =============================================================================
// PHASE COLORS
// =============================================================================

/**
 * Color palette for each structure phase.
 * Used for visual consistency across the beat matrix, arc visualization, and tabs.
 */
export const PHASE_COLORS: Record<StructurePhase, { bg: string; text: string; bgHighlight: string }> = {
  'Initial State': { bg: 'bg-slate-200 dark:bg-slate-700', text: 'text-slate-700 dark:text-slate-200', bgHighlight: 'bg-slate-100 dark:bg-slate-800' },
  'Inciting Incident': { bg: 'bg-amber-200 dark:bg-amber-700', text: 'text-amber-700 dark:text-amber-200', bgHighlight: 'bg-amber-50 dark:bg-amber-900/30' },
  'Break Into Two': { bg: 'bg-orange-200 dark:bg-orange-700', text: 'text-orange-700 dark:text-orange-200', bgHighlight: 'bg-orange-50 dark:bg-orange-900/30' },
  'Midpoint': { bg: 'bg-purple-200 dark:bg-purple-700', text: 'text-purple-700 dark:text-purple-200', bgHighlight: 'bg-purple-50 dark:bg-purple-900/30' },
  'Break Into Three': { bg: 'bg-red-200 dark:bg-red-700', text: 'text-red-700 dark:text-red-200', bgHighlight: 'bg-red-50 dark:bg-red-900/30' },
  'Climax': { bg: 'bg-blue-200 dark:bg-blue-700', text: 'text-blue-700 dark:text-blue-200', bgHighlight: 'bg-blue-50 dark:bg-blue-900/30' },
  'Final State': { bg: 'bg-emerald-200 dark:bg-emerald-700', text: 'text-emerald-700 dark:text-emerald-200', bgHighlight: 'bg-emerald-50 dark:bg-emerald-900/30' },
};
